import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { OrganizationType } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { getVisibleOrgTypeOptions } from "@/components/onboarding/constants";
import { OrganizationTypeGrid } from "@/components/organization/OrganizationTypeGrid";

// Sempre a organização pessoal (ver comentário do componente) — DENTAL_OFFICE aparece como
// "Cirurgião Dentista", não "Clínica Odontológica".
const visibleTypeOptions = getVisibleOrgTypeOptions(true);

type Props = {
  types: OrganizationType[];
};

/** Editor da área de atuação da organização pessoal — só faz sentido aqui (o usuário é sempre OWNER da sua pessoal). */
export function OrgTypeSection({ types }: Props) {
  const utils = trpc.useUtils();
  const [selected, setSelected] = useState<OrganizationType[]>(types);
  const [dirty, setDirty] = useState(false);

  if (!dirty && selected.join(",") !== types.join(",")) {
    setSelected(types);
  }

  const updateTypes = trpc.organization.updateTypes.useMutation({
    onSuccess: () => {
      utils.me.current.invalidate();
      toast.success("Área de atuação atualizada.");
      setDirty(false);
    },
    onError: (err) => toast.error(err.message || "Erro ao atualizar área de atuação."),
  });

  const toggle = (value: OrganizationType) => {
    setDirty(true);
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  return (
    <section className="rounded-2xl border border-zinc-100 bg-white p-6 shadow-sm dark:border-border dark:bg-card sm:p-8">
      <h2 className="text-sm font-semibold text-zinc-900 dark:text-foreground">
        Área de atuação
      </h2>
      <p className="mt-1 text-[12.5px] text-zinc-500 dark:text-muted-foreground">
        Define o que aparece no seu menu lateral. Você pode combinar mais de uma opção.
      </p>

      <div className="mt-4">
        <OrganizationTypeGrid options={visibleTypeOptions} selected={selected} onToggle={toggle} />
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={() => updateTypes.mutate({ types: selected })}
          disabled={updateTypes.isPending || !dirty || selected.length === 0}
          className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-[12.5px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-50"
        >
          {updateTypes.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Salvar
        </button>
      </div>
    </section>
  );
}
